import { Component, Inject, OnInit, Optional } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { forkJoin, Observable } from "rxjs";
import { finalize } from "rxjs/operators";
import { ToastService } from "../core";
import {
  ConfigurationApi,
  DocumentConfigs,
  DocumentConfigFilter,
  DocumentTypes,
  LookupApi,
  LookupField,
  OfficeEntity,
} from "../core/api.gen";
import { CommonService } from "../core/common.service";
import { DialogService } from "../core/dialog.service";
import { LoaderService } from "../core/loader.service";
import { LookupTypeKeys } from "../shared/lookup-type-keys";


@Component({
  selector: "eos-governance-doc-types",
  templateUrl: "./governance-doc-types.component.html",
  styleUrls: ["./governance-doc-types.component.css"],
})
export class GovernanceDocTypesComponent implements OnInit {
  activities: DocumentConfigs[] = [];
  activityInfo: DocumentConfigs;

  activityFilter = new UntypedFormGroup({
    country: new UntypedFormControl(null, [Validators.required]),
    officeLocation: new UntypedFormControl(null),
    officeLegalEntity: new UntypedFormControl(null),
  });

  countries = [];
  officeLocations = [];
  officeLocationsInfo = [];
  officeLegalEntities: OfficeEntity[] = [];
  officeLegalEntitiesInfo: OfficeEntity[] = [];
  documentTypes = [];

  constructor(
    private common: CommonService,
    private lookupApi: LookupApi,
    private loader: LoaderService,
    private dialogService: DialogService,
    private router: Router,
    private toast: ToastService,
    private configApi: ConfigurationApi,
  ) {}

  ngOnInit() {
    this.loadSupportingData()
      .pipe(finalize(() => this.loader.clear()))
      .subscribe((p) => {
        this.countries = p.countries.filter(i =>
          [1048, 1047, 1052, 1049, 1050, 1051, 1055, 1053, 13410].includes(i.id)
        );
        this.officeLocations = p.officeLocations;
        this.officeLegalEntities = p.officeLegalEntities;
        this.documentTypes = p.documentTypes;
      });
  }

  loadSupportingData() {
    this.loader.show();
    return forkJoin({
      countries: this.lookupApi.getLookupFieldsByTypes([LookupTypeKeys.OFFICE_LOCATION_COUNTRY]),
      officeLocations: this.lookupApi.getLookupFieldsByTypes([LookupTypeKeys.OFFICE_LOCATION]),
      officeLegalEntities: this.lookupApi.getOfficeEntities(undefined, undefined),
      documentTypes: this.configApi.getDocumentTypes(),
    });
  }

  private getDocumentConfigs(filter: DocumentConfigFilter): Observable<DocumentConfigs[]> {
    return this.configApi.getDocumentConfigs(filter);
  }

  private getDocumentConfigById(id: number): Observable<DocumentConfigs> {
    return this.configApi.getDocumentConfigById(id);
  }

  createActivity() {
    this.activityInfo = undefined;
    const countryId = this.activityFilter.controls.country.value ?? 0;
    this.loader.show();
    this.getDocumentConfigs(new DocumentConfigFilter())
      .pipe(finalize(() => this.loader.clear()))
      .subscribe((allConfigs) => {
        this.dialogService
          .showGovernanceDocConfigEdit(
            this.activityInfo, this.countries, this.officeLocations,
            this.officeLegalEntities, this.documentTypes, false, countryId,
            allConfigs
          )
          .result.then((result) => {
            if (result === "success") this.getMappings();
          });
      });
  }

  onSubmit() {
    this.getMappings();
  }

  getMappings() {
    if (this.activityFilter.valid) {
      this.loader.show();
      this.getDocumentConfigs(this.getFormData())
        .pipe(finalize(() => this.loader.clear()))
        .subscribe((resp) => {
          this.activities = resp;
        });
    }
  }

  onCountryChange($event) {
    this.activityFilter.controls.officeLocation.reset();
    this.activityFilter.controls.officeLegalEntity.reset();
    if ($event) {
      this.officeLocationsInfo = this.officeLocations.filter(i => i.parentFieldId === $event.id);
      this.officeLegalEntitiesInfo = this.officeLegalEntities.filter(i => i.parentFieldId === $event.id);
    } else {
      this.officeLocationsInfo = [];
      this.officeLegalEntitiesInfo = [];
    }
  }

  editActivity(id: number) {
    this.loader.show();
    forkJoin({
      config: this.getDocumentConfigById(id),
      allConfigs: this.getDocumentConfigs(new DocumentConfigFilter())
    })
    .pipe(finalize(() => this.loader.clear()))
    .subscribe(({ config, allConfigs }) => {
      if (config) {
        this.activityInfo = config;
        this.dialogService
          .showGovernanceDocConfigEdit(
            this.activityInfo, this.countries, this.officeLocations,
            this.officeLegalEntities, this.documentTypes, true, 0,
            allConfigs
          )
          .result.then((result) => {
            if (result === "success") this.getMappings();
          });
      }
    });
  }

  clearForm() {
    this.activityFilter.reset();
    this.officeLocationsInfo = [];
    this.officeLegalEntitiesInfo = [];
    this.activities = [];
  }

  getFormData(): DocumentConfigFilter {
    const form = this.activityFilter.getRawValue();
    return new DocumentConfigFilter({
      countryId: form.country ?? undefined,
      locationId: form.officeLocation ?? undefined,
      legalEntityId: form.officeLegalEntity ?? undefined,
    });
  }
}